import { UUID } from "crypto";

import { useAxios } from "@/lib/hooks";

import {
  acceptOrganisationInvitation,
  assignAssistantToUser,
  assignKnowledgeBaseToUser,
  cancelInvitation,
  createOrganisation,
  CreateOrganisationInput,
  getAvailableAssistants,
  getAvailableKnowledgeBases,
  getCurrentOrganisation,
  getCurrentUserRole,
  getOrganisation,
  getOrganisationUsers,
  getPendingInvitations,
  inviteUser,
  InviteUserInput,
  removeAssistantFromUser,
  removeKnowledgeBaseFromUser,
  updateOrganisation,
  UpdateOrganisationInput,
  updateUserActiveStatus,
  updateUserRole,
  UpdateUserRoleInput,
} from "./organisation";

export const useOrganisationApi = () => {
  const { axiosInstance } = useAxios();

  return {
    createOrganisation: async (input: CreateOrganisationInput) =>
      createOrganisation(input, axiosInstance),
    getCurrentOrganisation: async () => getCurrentOrganisation(axiosInstance),
    getOrganisation: async (organisationId: UUID) =>
      getOrganisation(organisationId, axiosInstance),
    getOrganisationUsers: async (organisationId: UUID) =>
      getOrganisationUsers(organisationId, axiosInstance),
    inviteUser: async (organisationId: UUID, input: InviteUserInput) =>
      inviteUser(organisationId, input, axiosInstance),
    acceptOrganisationInvitation: async (
      token: string,
      password: string | null,
    ) => acceptOrganisationInvitation(token, password, axiosInstance),
    updateUserRole: async (
      organisationId: UUID,
      userId: UUID,
      input: UpdateUserRoleInput,
    ) => updateUserRole(organisationId, userId, input, axiosInstance),
    updateUserActiveStatus: async (
      organisationId: UUID,
      userId: UUID,
      active: boolean,
    ) => updateUserActiveStatus(organisationId, userId, active, axiosInstance),
    getPendingInvitations: async (organisationId: UUID) =>
      getPendingInvitations(organisationId, axiosInstance),
    cancelInvitation: async (organisationId: UUID, invitationId: UUID) =>
      cancelInvitation(organisationId, invitationId, axiosInstance),
    getCurrentUserRole: async () => getCurrentUserRole(axiosInstance),
    updateOrganisation: async (
      organisationId: UUID,
      input: UpdateOrganisationInput,
    ) => updateOrganisation(organisationId, input, axiosInstance),
    // Access management APIs
    assignAssistantToUser: async (
      organisationId: UUID,
      userId: UUID,
      assistantId: string,
    ) =>
      assignAssistantToUser(organisationId, userId, assistantId, axiosInstance),
    removeAssistantFromUser: async (
      organisationId: UUID,
      userId: UUID,
      assistantId: string,
    ) =>
      removeAssistantFromUser(
        organisationId,
        userId,
        assistantId,
        axiosInstance,
      ),
    assignKnowledgeBaseToUser: async (
      organisationId: UUID,
      userId: UUID,
      knowledgeBaseId: string,
    ) =>
      assignKnowledgeBaseToUser(
        organisationId,
        userId,
        knowledgeBaseId,
        axiosInstance,
      ),
    removeKnowledgeBaseFromUser: async (
      organisationId: UUID,
      userId: UUID,
      knowledgeBaseId: string,
    ) =>
      removeKnowledgeBaseFromUser(
        organisationId,
        userId,
        knowledgeBaseId,
        axiosInstance,
      ),
    getAvailableAssistants: async (organisationId: UUID) =>
      getAvailableAssistants(organisationId, axiosInstance),
    getAvailableKnowledgeBases: async (organisationId: UUID) =>
      getAvailableKnowledgeBases(organisationId, axiosInstance),
  };
};
